import type { DealFieldDef, RawDealFile } from '@/services/dealFile'
import { readFieldValue } from '@/services/dealFile'
import type { DossierCoverage, DossierField } from '@/services/ingestion'
import { LOAN_UPDATE_COLUMNS } from './editableFields'
import { mergeDealFile } from './fromLoan'

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

/** Whether the value is the deal's own reading: stated by the deal's source
 *  record, or derived only from such values. A dossier from before the server
 *  named its sources says nothing, and is taken as the deal's. */
function statedByThisDeal(field: DossierField, coverage: DossierCoverage): boolean {
  const own = coverage.ingestionId
  if (!own) return true
  const ok = (row: DossierField | undefined) =>
    !row || !Array.isArray(row.statedBy) || row.statedBy.includes(own)
  if (field.status === 'stated' || field.status === 'confirmed') return ok(field)
  const byName = new Map(coverage.groups.flatMap((g) => g.fields).map((f) => [f.field, f]))
  return (field.inputs ?? []).every((input) => ok(byName.get(input.field)))
}

/** Fill gaps on the loan row from the ingestion dossier projection — never overwrite stated loan values. */
export function overlayIngestionDossier(
  base: RawDealFile | undefined,
  coverage: DossierCoverage | undefined,
  defs: DealFieldDef[],
): RawDealFile | undefined {
  if (!base || !coverage) return base

  const patch: RawDealFile = { id: base.id, custom_fields: {} }
  const custom = { ...(typeof base.custom_fields === 'object' ? base.custom_fields : {}) } as Record<
    string,
    unknown
  >

  // Fields the deal holds for review (R5): a later email states them and a
  // person has not yet accepted it. The intake dossier merges that email and
  // calls its figures stated, but the deal keeps its current value — blank
  // included — until someone applies the revision.
  const rows = base.pending_revisions ?? base.pendingRevisions
  const pending = new Set(
    (Array.isArray(rows) ? rows : [])
      .map((row) => (row && typeof row === 'object' ? (row as { field?: unknown }).field : null))
      .filter((name): name is string => typeof name === 'string'),
  )

  for (const group of coverage.groups) {
    for (const field of group.fields) {
      if (pending.has(field.field)) continue
      // Only what the record READ or computed from what it read. A suggestion
      // — a country from the state code, a market-convention payment
      // frequency — is not data until a person confirms it, so it never
      // fills a deal field on screen.
      if (field.status !== 'stated' && field.status !== 'confirmed' && field.status !== 'derived') continue
      // Only the deal's own email. Another email in the group reaches the
      // deal when a person applies it (or the thread path writes it); one a
      // person kept the deal's value over must not fill the gap here — The
      // Marlowe showed a kept-blank Floor as the held term sheet's 3%.
      if (!statedByThisDeal(field, coverage)) continue
      if (isEmpty(field.value)) continue
      const def = defs.find((d) => d.name === field.field)
      // Where a value goes is the catalogue's `native` flag — the same rule
      // `readFieldValue` uses to find it again. LOAN_UPDATE_COLUMNS answers a
      // different question: which keys `PUT /loans/{id}` accepts at the top
      // level of the request body. Six of those (noi, sf, units, year_built,
      // property_value, debt_yield) are *aliases* the API unpacks into
      // custom_fields, so routing by the write schema wrote them where nothing
      // reads them: an in-place NOI lifted out of the sponsor's workbook, with
      // lineage and a source document behind it, still rendered "Not recorded".
      // The write schema decides only for a field the catalogue does not know.
      const native = def ? def.native : LOAN_UPDATE_COLUMNS.has(field.field)
      const current = readFieldValue(base, def ?? ({ name: field.field, native } as DealFieldDef))
      if (!isEmpty(current)) continue

      if (native) {
        patch[field.field] = field.value
      } else {
        custom[field.field] = field.value
      }
    }
  }

  if (Object.keys(custom).length > 0) patch.custom_fields = custom
  if (Object.keys(patch).length <= 2 && !patch.custom_fields) return base
  return mergeDealFile(base, patch)
}
